/**
 * SaldoInicialCxPModal — Saldos Iniciales de proveedores (CxP).
 * Espejo del modal de Saldos Iniciales de CxC, pero contra proveedor.
 *   · Pendiente = le debemos al proveedor (FacturaCxP, saldo positivo)
 *   · A Favor   = anticipo a favor (CuentaXPagar con saldo negativo)
 * Requiere que el proveedor (cuenta de Pasivo) ya exista en el catálogo.
 */
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { uploadExcel } from "../../shared/excelService";

export default function SaldoInicialCxPModal({ onClose, onSuccess }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("importar");
  const [proveedores, setProveedores] = useState([]);
  const [saldos, setSaldos] = useState([]);
  const [loadingSaldos, setLoadingSaldos] = useState(false);

  const [downloading, setDownloading]   = useState(false);
  const [uploading, setUploading]       = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const FORM_INIT = {
    proveedor: "", tipo: "pendiente", descripcion: "",
    fecha: "", monto: "", moneda: "CRC", tipo_cambio: "1",
  };
  const [showForm, setShowForm]   = useState(false);
  const [editingPk, setEditingPk] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [formMsg, setFormMsg] = useState(null);
  const [form, setForm]       = useState(FORM_INIT);

  useEffect(() => {
    apiFetch("/api/finance/catalogo/detalle/?tipo=PASIVO&padre_nombre=Cuentas+por+Pagar+Locales&page_size=500")
      .then((r) => r.json())
      .then((d) => {
        const list = Array.isArray(d) ? d : (d.results ?? []);
        setProveedores(list);
        setForm((f) => ({ ...f, proveedor: list[0] ? String(list[0].id) : "" }));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "gestionar") loadSaldos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  function loadSaldos() {
    setLoadingSaldos(true);
    apiFetch("/api/finance/saldo-inicial-cxp/")
      .then((r) => r.json())
      .then((data) => setSaldos(Array.isArray(data) ? data : []))
      .catch(() => setSaldos([]))
      .finally(() => setLoadingSaldos(false));
  }

  async function downloadPlantilla() {
    setDownloading(true);
    try {
      const res  = await apiFetch("/api/finance/saldo-inicial-cxp/plantilla/");
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = "plantilla_saldos_iniciales_cxp.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (_) {} finally { setDownloading(false); }
  }

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadResult(null);
    try {
      const data = await uploadExcel("/api/finance/saldo-inicial-cxp/importar/", file);
      setUploadResult(data);
      if ((data.creados ?? 0) > 0) onSuccess?.();
    } catch (_) {
      setUploadResult({ creados: 0, errores: [t("cxp.errorGuardar")], omitidos: 0 });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleSave() {
    if (!form.proveedor || !form.tipo || !form.descripcion || !form.fecha || !form.monto) {
      setFormMsg({ ok: false, text: t("saldoInicialCxp.camposReq") });
      return;
    }
    setSaving(true); setFormMsg(null);
    try {
      const url = editingPk
        ? `/api/finance/saldo-inicial-cxp/${editingPk}/?tipo=${form.tipo}`
        : "/api/finance/saldo-inicial-cxp/";
      const res = await apiFetch(url, {
        method: editingPk ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setFormMsg({ ok: false, text: err.error || t("cxp.errorGuardar") });
        return;
      }
      setFormMsg({ ok: true, text: editingPk ? t("common.actualizado") : t("saldoInicialCxp.creadoOk") });
      setShowForm(false); setEditingPk(null);
      setForm({ ...FORM_INIT, proveedor: proveedores[0] ? String(proveedores[0].id) : "" });
      loadSaldos(); onSuccess?.();
      setTimeout(() => setFormMsg(null), 3500);
    } catch (_) {
      setFormMsg({ ok: false, text: t("cxp.errorGuardar") });
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!deletingItem) return;
    try {
      await apiFetch(`/api/finance/saldo-inicial-cxp/${deletingItem.pk}/?tipo=${deletingItem.tipo}`,
        { method: "DELETE" });
      setDeletingItem(null); loadSaldos(); onSuccess?.();
    } catch (_) {}
  }

  function startEdit(item) {
    setEditingPk(item.pk);
    setForm({
      proveedor:   String(item.proveedor_id),
      tipo:        item.tipo,
      descripcion: item.descripcion || "",
      fecha:       item.fecha || "",
      monto:       String(item.monto),
      moneda:      item.moneda || "CRC",
      tipo_cambio: String(item.tipo_cambio ?? 1),
    });
    setShowForm(true); setFormMsg(null);
  }

  function cancelForm() {
    setShowForm(false); setEditingPk(null);
    setForm({ ...FORM_INIT, proveedor: proveedores[0] ? String(proveedores[0].id) : "" });
    setFormMsg(null);
  }

  const inputSt = {
    width: "100%", padding: "7px 10px", background: "var(--bg-input)",
    border: "1px solid var(--border-card)", borderRadius: 6,
    color: "var(--text-primary)", fontSize: "0.84rem", colorScheme: "light dark", outline: "none",
  };
  const labelSt = {
    display: "block", color: "var(--text-muted)", fontSize: "0.71rem", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4,
  };

  return (
    <>
      <div onClick={deletingItem ? undefined : onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 1050, backdropFilter: "blur(3px)" }}/>

      {deletingItem && (
        <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
          background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 12,
          padding: "24px 28px", zIndex: 1200, width: "min(400px,92vw)", boxShadow: "0 24px 64px rgba(0,0,0,0.6)" }}>
          <p style={{ color: "var(--text-primary)", fontWeight: 600, marginBottom: 8 }}>
            {t("saldoInicialCxp.eliminarTitulo")}
          </p>
          <p style={{ color: "var(--text-muted)", fontSize: "0.84rem", marginBottom: 20 }}>
            <strong>{deletingItem.proveedor}</strong> — {deletingItem.descripcion}{" "}
            ({deletingItem.tipo === "favor" ? t("saldoInicialCxp.aFavor") : t("saldoInicialCxp.pendiente")})
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={() => setDeletingItem(null)} style={{ padding: "7px 16px", background: "transparent",
              border: "1px solid var(--border-card)", borderRadius: 6, color: "var(--text-muted)", cursor: "pointer" }}>
              {t("common.cancelar")}
            </button>
            <button onClick={handleDelete} style={{ padding: "7px 16px", background: "#ef4444", color: "#fff",
              border: "none", borderRadius: 6, fontWeight: 600, cursor: "pointer" }}>
              {t("common.eliminar")}
            </button>
          </div>
        </div>
      )}

      <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
        background: "var(--bg-card)", border: "1px solid var(--border-card)", borderRadius: 14,
        width: "min(760px,95vw)", maxHeight: "88vh", overflow: "hidden", display: "flex",
        flexDirection: "column", zIndex: 1100, boxShadow: "0 24px 64px rgba(0,0,0,0.55)" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "18px 24px 14px", borderBottom: "1px solid var(--border-card)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.3)",
              borderRadius: 9, padding: "9px 11px", color: "#a78bfa" }}>
              <i className="fa-solid fa-scale-balanced" style={{ fontSize: "1.1rem" }}/>
            </div>
            <div>
              <h3 style={{ margin: 0, color: "var(--text-primary)", fontSize: "1rem", fontWeight: 700 }}>
                {t("saldoInicialCxp.titulo")}
              </h3>
              <p style={{ margin: "2px 0 0", color: "var(--text-muted)", fontSize: "0.77rem" }}>
                {t("saldoInicialCxp.subtitulo")}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text-muted)",
            cursor: "pointer", fontSize: "1.1rem", lineHeight: 1, padding: "2px 5px" }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid var(--border-card)", flexShrink: 0, padding: "0 24px" }}>
          {[
            { id: "importar",  icon: "fa-file-arrow-up", label: t("saldoInicialCxp.tabImportar") },
            { id: "gestionar", icon: "fa-list-check",    label: t("saldoInicialCxp.tabGestionar") },
          ].map(({ id, icon, label }) => (
            <button key={id} onClick={() => { setTab(id); setFormMsg(null); }}
              style={{ background: "none", border: "none",
                borderBottom: tab === id ? "2px solid #a78bfa" : "2px solid transparent",
                color: tab === id ? "#a78bfa" : "var(--text-muted)", fontWeight: tab === id ? 700 : 400,
                fontSize: "0.84rem", padding: "10px 16px", cursor: "pointer", marginBottom: -1 }}>
              <i className={`fa-solid ${icon}`} style={{ marginRight: 7 }}/>{label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ overflowY: "auto", flex: 1, padding: "20px 24px" }}>

          {/* TAB IMPORTAR */}
          {tab === "importar" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div style={{ background: "rgba(139,92,246,0.07)", border: "1px solid rgba(139,92,246,0.2)",
                borderRadius: 8, padding: "12px 16px", fontSize: "0.83rem", color: "var(--text-secondary)", lineHeight: 1.55 }}>
                <i className="fa-solid fa-circle-info" style={{ color: "#a78bfa", marginRight: 8 }}/>
                {t("saldoInicialCxp.infoImportar")}
              </div>

              <div>
                <p style={{ color: "var(--text-muted)", fontSize: "0.78rem", fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>
                  {t("saldoInicialCxp.paso1")}
                </p>
                <button onClick={downloadPlantilla} disabled={downloading}
                  style={{ padding: "9px 20px", background: "rgba(139,92,246,0.15)",
                    border: "1px solid rgba(139,92,246,0.4)", color: "#a78bfa", borderRadius: 7,
                    fontWeight: 600, fontSize: "0.84rem", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
                  {downloading
                    ? <><i className="fa-solid fa-circle-notch fa-spin"/> {t("common.descargando")}…</>
                    : <><i className="fa-solid fa-download"/> {t("saldoInicialCxp.descargarPlantilla")}</>}
                </button>
              </div>

              <div>
                <p style={{ color: "var(--text-muted)", fontSize: "0.78rem", fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>
                  {t("saldoInicialCxp.paso2")}
                </p>
                <input type="file" accept=".xlsx,.xls" ref={fileInputRef} onChange={handleFileChange}
                  style={{ display: "none" }} id="si-cxp-file-input"/>
                <label htmlFor="si-cxp-file-input" style={{ display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "9px 20px", background: uploading ? "rgba(139,92,246,0.08)" : "rgba(139,92,246,0.15)",
                  border: "1px solid rgba(139,92,246,0.4)", color: "#a78bfa", borderRadius: 7,
                  fontWeight: 600, fontSize: "0.84rem", cursor: uploading ? "not-allowed" : "pointer" }}>
                  {uploading
                    ? <><i className="fa-solid fa-circle-notch fa-spin"/> {t("common.importando")}…</>
                    : <><i className="fa-solid fa-file-arrow-up"/> {t("saldoInicialCxp.seleccionarArchivo")}</>}
                </label>
              </div>

              {uploadResult && (
                <div style={{ background: uploadResult.creados > 0 ? "rgba(74,222,128,0.07)" : "rgba(248,113,113,0.07)",
                  border: `1px solid ${uploadResult.creados > 0 ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}`,
                  borderLeft: `4px solid ${uploadResult.creados > 0 ? "#4ade80" : "#f87171"}`,
                  borderRadius: 8, padding: "14px 16px" }}>
                  <p style={{ fontWeight: 700, fontSize: "0.88rem", margin: "0 0 6px",
                    color: uploadResult.creados > 0 ? "#86efac" : "#fca5a5" }}>
                    {uploadResult.creados > 0
                      ? t("saldoInicialCxp.importadosOk", { n: uploadResult.creados })
                      : t("saldoInicialCxp.ningunImportado")}
                    {uploadResult.omitidos > 0 && ` · ${t("saldoInicialCxp.omitidos", { n: uploadResult.omitidos })}`}
                  </p>
                  {uploadResult.errores?.length > 0 && (
                    <ul style={{ margin: 0, paddingLeft: 18, color: "#fca5a5", fontSize: "0.8rem" }}>
                      {uploadResult.errores.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          {/* TAB GESTIONAR */}
          {tab === "gestionar" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {formMsg && (
                <div style={{ padding: "9px 14px", borderRadius: 7, fontSize: "0.84rem", fontWeight: 600,
                  background: formMsg.ok ? "rgba(74,222,128,0.1)" : "rgba(248,113,113,0.1)",
                  border: `1px solid ${formMsg.ok ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}`,
                  color: formMsg.ok ? "#4ade80" : "#f87171" }}>
                  <i className={`fa-solid ${formMsg.ok ? "fa-circle-check" : "fa-circle-exclamation"}`} style={{ marginRight: 7 }}/>
                  {formMsg.text}
                </div>
              )}

              {showForm ? (
                <div style={{ background: "var(--hover-bg)", border: "1px solid var(--border-card)", borderRadius: 10, padding: "16px 18px" }}>
                  <p style={{ color: "#a78bfa", fontWeight: 700, fontSize: "0.88rem", marginBottom: 14 }}>
                    <i className={`fa-solid ${editingPk ? "fa-pen" : "fa-plus"}`} style={{ marginRight: 7 }}/>
                    {editingPk ? t("saldoInicialCxp.editar") : t("saldoInicialCxp.nuevo")}
                  </p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={labelSt}>{t("cxp.proveedor")}</label>
                      <select value={form.proveedor} disabled={!!editingPk}
                        onChange={(e) => setForm((f) => ({ ...f, proveedor: e.target.value }))}
                        style={{ ...inputSt, cursor: editingPk ? "not-allowed" : "pointer" }}>
                        {proveedores.map((p) => (
                          <option key={p.id} value={String(p.id)}>{p.codigo} — {p.nombre}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={labelSt}>{t("saldoInicialCxp.tipo")}</label>
                      <select value={form.tipo} onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value }))}
                        style={{ ...inputSt, cursor: "pointer" }}>
                        <option value="pendiente">{t("saldoInicialCxp.pendienteOpt")}</option>
                        <option value="favor">{t("saldoInicialCxp.favorOpt")}</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelSt}>{t("saldoInicialCxp.fecha")}</label>
                      <input type="text" placeholder="31/01/2026" value={form.fecha}
                        onChange={(e) => setForm((f) => ({ ...f, fecha: e.target.value }))} style={inputSt}/>
                    </div>
                    <div>
                      <label style={labelSt}>{t("cxp.monto")}</label>
                      <input type="number" min="0" step="0.01" placeholder="0.00" value={form.monto}
                        onChange={(e) => setForm((f) => ({ ...f, monto: e.target.value }))} style={inputSt}/>
                    </div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <label style={labelSt}>{t("cxp.descripcionLabel")}</label>
                      <input type="text" placeholder={t("saldoInicialCxp.descripcionPh")} value={form.descripcion}
                        onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))} style={inputSt}/>
                    </div>
                    <div>
                      <label style={labelSt}>{t("common.moneda")}</label>
                      <select value={form.moneda} onChange={(e) => setForm((f) => ({ ...f, moneda: e.target.value }))}
                        style={{ ...inputSt, cursor: "pointer" }}>
                        <option value="CRC">CRC — {t("common.colones")}</option>
                        <option value="USD">USD — {t("common.dolares")}</option>
                      </select>
                    </div>
                    {form.moneda === "USD" && (
                      <div>
                        <label style={labelSt}>{t("common.tipoCambio")}</label>
                        <input type="number" min="1" step="0.01" placeholder="510.00" value={form.tipo_cambio}
                          onChange={(e) => setForm((f) => ({ ...f, tipo_cambio: e.target.value }))} style={inputSt}/>
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14 }}>
                    <button onClick={cancelForm} style={{ padding: "7px 16px", background: "transparent",
                      border: "1px solid var(--border-card)", borderRadius: 6, color: "var(--text-muted)",
                      cursor: "pointer", fontSize: "0.84rem" }}>{t("common.cancelar")}</button>
                    <button onClick={handleSave} disabled={saving} style={{ padding: "7px 18px",
                      background: saving ? "rgba(139,92,246,0.5)" : "#7c3aed", border: "none", borderRadius: 6,
                      color: "#fff", fontWeight: 600, fontSize: "0.84rem", cursor: saving ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: 7 }}>
                      {saving
                        ? <><i className="fa-solid fa-circle-notch fa-spin"/> {t("cxp.guardando")}</>
                        : <><i className="fa-solid fa-floppy-disk"/> {editingPk ? t("common.guardarCambios") : t("common.crear")}</>}
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => { setShowForm(true); setEditingPk(null); setFormMsg(null);
                  setForm({ ...FORM_INIT, proveedor: proveedores[0] ? String(proveedores[0].id) : "" }); }}
                  style={{ alignSelf: "flex-start", padding: "8px 16px", background: "rgba(139,92,246,0.15)",
                    border: "1px solid rgba(139,92,246,0.35)", color: "#a78bfa", borderRadius: 7,
                    fontWeight: 600, fontSize: "0.84rem", cursor: "pointer", display: "flex", alignItems: "center", gap: 7 }}>
                  <i className="fa-solid fa-plus"/> {t("saldoInicialCxp.agregar")}
                </button>
              )}

              {loadingSaldos ? (
                <div style={{ textAlign: "center", padding: "30px 0", color: "var(--text-muted)" }}>
                  <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 8 }}/>{t("common.cargando")}…
                </div>
              ) : saldos.length === 0 ? (
                <div style={{ textAlign: "center", padding: "30px 0", color: "var(--text-muted)", fontSize: "0.85rem" }}>
                  <i className="fa-solid fa-inbox" style={{ fontSize: "1.8rem", marginBottom: 8, display: "block" }}/>
                  {t("saldoInicialCxp.sinRegistros")}
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.83rem" }}>
                    <thead>
                      <tr style={{ background: "var(--hover-bg)" }}>
                        {[t("cxp.proveedor"), t("saldoInicialCxp.tipo"), t("saldoInicialCxp.fecha"),
                          t("cxp.descripcionLabel"), t("cxp.monto"), ""].map((h, i) => (
                          <th key={i} style={{ padding: "9px 12px", textAlign: "left", color: "var(--text-muted)",
                            fontWeight: 700, fontSize: "0.75rem", textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {saldos.map((s, i) => (
                        <tr key={`${s.tipo}-${s.pk}`} style={{ borderBottom: "1px solid var(--border-card)",
                          background: i % 2 === 0 ? "transparent" : "var(--hover-bg)" }}>
                          <td style={{ padding: "9px 12px", fontWeight: 700, color: "var(--text-primary)" }}>{s.proveedor}</td>
                          <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                            <span style={{ padding: "2px 9px", borderRadius: 20, fontSize: "0.75rem", fontWeight: 700,
                              background: s.tipo === "favor" ? "rgba(74,222,128,0.12)" : "rgba(251,146,60,0.12)",
                              color: s.tipo === "favor" ? "#4ade80" : "#fb923c",
                              border: `1px solid ${s.tipo === "favor" ? "rgba(74,222,128,0.3)" : "rgba(251,146,60,0.3)"}` }}>
                              {s.tipo === "favor" ? t("saldoInicialCxp.aFavor") : t("saldoInicialCxp.pendiente")}
                            </span>
                          </td>
                          <td style={{ padding: "9px 12px", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>{s.fecha}</td>
                          <td style={{ padding: "9px 12px", color: "var(--text-secondary)", maxWidth: 220,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {s.descripcion || <span style={{ color: "var(--text-muted)" }}>—</span>}
                          </td>
                          <td style={{ padding: "9px 12px", fontWeight: 700, whiteSpace: "nowrap",
                            color: s.tipo === "favor" ? "#4ade80" : "#fb923c" }}>
                            {(s.moneda || "CRC") === "USD" ? "$" : "₡"}
                            {Number(s.monto).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td style={{ padding: "9px 12px", whiteSpace: "nowrap", textAlign: "right" }}>
                            <button onClick={() => startEdit(s)} title={t("common.editar")}
                              style={{ background: "none", border: "none", color: "#60a5fa", cursor: "pointer", marginRight: 10 }}>
                              <i className="fa-solid fa-pen"/>
                            </button>
                            <button onClick={() => setDeletingItem(s)} title={t("common.eliminar")}
                              style={{ background: "none", border: "none", color: "#f87171", cursor: "pointer" }}>
                              <i className="fa-solid fa-trash"/>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
